import React from 'react'
import { Link } from 'react-router-dom'
import { JsonParam, useQueryParams, withDefault } from 'use-query-params'

import Button from '@/gf/components/ButtonOld'
import usePage from '@/gf/hooks/usePage'
import useToggle from '@/gf/hooks/useToggle'
import FilterSet from '@/gf/modules/FilterSet'
import FormatFilters from '@/gf/modules/FormatFilters'
import UserContext from '@/gf/modules/User'
import { useStoreOrderAddedSubscription } from '@/suppliers/_gen/gql'
import useApolloWsClient from '@/suppliers/hooks/useApolloWsClient'
import useSearchStoreOrders from '../hooks/useSearchStoreOrdersNext'
import useSession from '../hooks/useSession'

import ModalForm from '@/gf/components/ModalForm'
import Page from '@/gf/components/Page'
import useConfig from '@/gf/hooks/useConfig'
import Frame from '../components/Frame'
import StoreOrdersTable from '../components/StoreOrdersTable'

import { Filter } from '../../types'

const ExportModal = ({ open, onClose, filters, count }) => {
  const config = useConfig()

  return (
    <ModalForm
      open={open}
      onClose={onClose}
      title="Export Users"
      action="/csv/download/supplier_orders_"
      method="POST"
      onSubmit={(_e) => null}
    >
      <p className="pt-4">
        <input name="filters" type="hidden" value={JSON.stringify(filters)} />
        <input name="_csrf_token" type="hidden" value={config.csrfToken} />
        <span>Click export to download a CSV with {count} orders</span>
      </p>
    </ModalForm>
  )
}

const breadcrumbs = {
  copy: 'Back to Dashboard',
  crumbs: [{ name: 'Orders', href: '/orders' }],
}

const useQueryParamsOpts = {
  filters: withDefault<Filter[], Filter[]>(JsonParam, []),
}

const Orders = () => {
  const [exportModalOpen, exportModalToggler] = useToggle()
  const [newOrders, setNewOrders] = React.useState(false)
  const { store, user } = useSession()
  const [page, updatePage] = usePage()
  const [query, updateQuery] = useQueryParams(useQueryParamsOpts)

  const defaultFilters = FilterSet.toApiFilters(query.filters)

  const filters = UserContext.isAdmin(user)
    ? // Admin
      FormatFilters.andFilters(defaultFilters)
    : // Not Admin
      store.onboardedSuppDashDatetime
      ? FormatFilters.andFilters([
          ['equals', 'storeId', store.id],
          ['greater_than', 'inserted_at', store.onboardedSuppDashDatetime],
          ...defaultFilters,
        ])
      : FormatFilters.andFilters([['equals', 'storeId', store.id], ...defaultFilters])

  const variables = { page, filters }
  const searchResult = useSearchStoreOrders({ variables, fetchPolicy: 'no-cache' })
  const search = searchResult.data?.storeOrdersSearch

  useStoreOrderAddedSubscription({
    variables: { storeId: store.id },
    client: useApolloWsClient(),
    onData: () => setNewOrders(true),
  })

  const reload = () => {
    setNewOrders(false)
    searchResult.refetch(variables)
  }

  const updateFilters = (newFilters: Filter[]) => {
    updatePage(1)
    updateQuery({ filters: newFilters })
  }

  const totalOrders = search?.pagination?.totalResults || 0

  return (
    <Frame breadcrumbs={breadcrumbs}>
      <ExportModal
        open={exportModalOpen}
        onClose={exportModalToggler.off}
        filters={filters}
        count={totalOrders}
      />
      <Page
        title="Orders"
        titleRightContent={
          <div className="flex flex-horizontal justify-end gap-x-2 text-sm text-gray-500">
            {store.showExportOrders && (
              <Button title="Export" primary={false} onClick={() => exportModalToggler.on()} />
            )}
            <Link
              to="/orders/create"
              className="text-white border-transparent bg-gearflow hover:bg-yellow-700 focus:ring-yellow-500 inline-flex items-center px-4 py-2 border text-sm rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2"
            >
              Create Quote
            </Link>
          </div>
        }
      >
        {newOrders && (
          <div className="flex gap-2 text-sm text-gray-500">
            <span>You have new order(s)!</span>
            <button className="text-blue-500 underline" onClick={() => reload()} type="button">
              Refresh list
            </button>
          </div>
        )}
        {searchResult.error ? (
          <p className="mt-4 text-sm text-gray-700">
            Error fetching Orders, please contact support if this persists.
          </p>
        ) : (
          <StoreOrdersTable
            page={page}
            filters={query.filters}
            updatePage={updatePage}
            updateFilters={updateFilters}
            storeOrders={search?.storeOrders}
            pagination={search?.pagination}
            admin={UserContext.isAdmin(user)}
          />
        )}
      </Page>
    </Frame>
  )
}

export default Orders
